import React, { useEffect } from "react";
import { PaymentMethod } from "../../http/protocol";
import "./styles/Deposit.scss";
import { CashierSection } from "../../payments/PaymentsReducer";
import { t } from "../../i18n";
import { AppState } from "../../RootReducer";
import { useDispatch, useSelector } from "react-redux";
import { getActiveCurrency } from "../../selectors/user-selectors";
import { getActiveGroup, getMethods, getPreferredMethod, getSection } from "../../selectors/payments-selectors";
import { selectGroup, selectMethod } from "../../payments/PaymentsActions";
import { RecommendedWalletIcon } from "../buttons/RecommendedWalletIcon";
import { AnyAction } from "redux";

export const ChooseMethod: React.FC = () => {
  const dispatch = useDispatch();
  const methods = useSelector((state: AppState) => getMethods(state));
  const activeGroup = useSelector((state: AppState) => getActiveGroup(state));
  const activeSiteCurrency = useSelector((state: AppState) => getActiveCurrency(state));
  const section = useSelector((state: AppState) => getSection(state));
  const preferredMethod = useSelector((state: AppState) => getPreferredMethod(state));
  const selectedBundleId = useSelector((state: AppState) => state.store.selectedBundleId);

  const handleSelectMethod = (method: PaymentMethod, currencyCode: string) => {
    dispatch(selectMethod(method, currencyCode) as unknown as AnyAction);
  };

  const handleSelectGroup = (groupName: string) => {
    dispatch(selectGroup(groupName) as unknown as AnyAction);
  };

  useEffect(() => {
    if (methods?.length === 1 && activeSiteCurrency?.code) {
      if (methods[0].group) {
        handleSelectGroup(methods[0].group);
      } else {
        handleSelectMethod(methods[0], activeSiteCurrency.code);
      }
    } else if (preferredMethod && activeSiteCurrency?.code) {
      if (preferredMethod.group) {
        handleSelectGroup(preferredMethod.group);
      } else {
        handleSelectMethod(preferredMethod, activeSiteCurrency.code);
      }
    }
  }, [methods, activeSiteCurrency, preferredMethod]);

  const getMethodDisplayCode = (method: PaymentMethod): { code: string; isGroup: boolean } => {
    if (!activeGroup && method.group) {
      return { code: method.group || "", isGroup: true };
    }
    return { code: method.code || "", isGroup: false };
  };

  let renderedGroups: string[] = [];
  const filteredMethods = methods?.filter((m: PaymentMethod) => (activeGroup ? m.group === activeGroup : true)) ?? [];
  const lastUsedIndex = filteredMethods?.findIndex((m) => m.isLatest);

  if (lastUsedIndex !== undefined && lastUsedIndex > 0) {
    const [lastUsedMethod] = filteredMethods.splice(lastUsedIndex, 1);
    filteredMethods.unshift(lastUsedMethod);
  }

  return (
    <div className="chooseMethod" data-testid="choose-method">
      <div className="wrapper">
        {filteredMethods?.map((method: PaymentMethod, index: number) => {
          if (activeGroup) {
            //do nothing
          } else if (method.group && renderedGroups.find((g) => g === method.group)) {
            return false;
          } else if (method.group && method.group.length > 0) {
            renderedGroups.push(method.group);
          }

          const { code, isGroup } = getMethodDisplayCode(method);
          const recommended = method.recommendedWalletURL ? "show" : "hide";

          return (
            <div
              key={index}
              className="col-12 methodItem"
              onClick={() => activeSiteCurrency?.code && (isGroup ? handleSelectGroup(code || "") : handleSelectMethod(method, activeSiteCurrency.code))}
              data-testid={`payment-method-${isGroup ? "group" : "item"}-${code?.toLowerCase()}`}
              role={isGroup ? "button" : "option"}
              aria-label={isGroup ? `Select ${code} group` : `Select ${method.name} payment method`}
            >
              {method.zeroNetworkFee && section == CashierSection.WITHDRAW && <BestOptionRibbon />}
              <div className="payment-select-wrapper">
                <div className="payment-icon">{method.code && <img className={`img method-${method.code.toLowerCase()}`} alt={`${method.name} icon`} data-testid={`payment-icon-${method.code.toLowerCase()}`} />}</div>
                <div className="payment-selection">
                  <div className="selection-wrapper">
                    <div>
                      <p className="payment-select-name">
                        {t(`payments.method.${code}.name`, undefined, isGroup ? undefined : method.name)}
                        {method.isLatest && (
                          <span className="payment-last-used" data-testid="last-used-indicator">
                            {t("payments.last-used")}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="description">
                    <span className="payment-select-helper-text">
                      {section === CashierSection.DEPOSIT && t(`payments.method.${code}.select_help_text.${selectedBundleId ? "purchase" : "deposit"}`)}
                      {section === CashierSection.WITHDRAW && t(`payments.method.${code}.select_help_text.withdrawal`)}
                      {method.recommendedWalletURL && (
                        <button className={`recommend ${recommended}`} data-testid={`recommended-wallet-${code?.toLowerCase()}`}>
                          <a href={method.recommendedWalletURL} target="_blank" className="recommend-link" data-testid="recommended-wallet-link">
                            {t("payments.recommendedWallet")}&nbsp;&nbsp;
                            <RecommendedWalletIcon />
                          </a>
                        </button>
                      )}
                    </span>
                  </div>
                </div>
                <div className="btnWrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 21" className="arrow" aria-hidden="true">
                    <path fillRule="nonzero" d="M3.621 10.5l7.94-7.94A1.5 1.5 0 0 0 9.439.44l-9 9a1.5 1.5 0 0 0 0 2.12l9 9a1.5 1.5 0 0 0 2.122-2.12L3.62 10.5z" />
                  </svg>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BestOptionRibbon = () => {
  return (
    <div className="ribbon" data-testid="zero-network-fee-ribbon">
      {t("withdraw.zero-network-fee")}
    </div>
  );
};

export default ChooseMethod;
